import { MagnifyingGlassIcon, SortAscendingIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { times } from "lodash-es";
import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { Autocomplete } from "@/components/Autocomplete/Autocomplete";
import { ButtonBase } from "@/components/ButtonBase/ButtonBase";
import { DrawerTopBar } from "@/components/Drawer/DrawerTopBar";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import { ErrorPanel } from "@/components/Errors/ErrorPanel";
import { IconAirplaneCrossed } from "@/components/Icons";
import { Skeleton } from "@/components/Skeleton/Skeleton";
import {
  TripCard,
  TripCardSkeleton,
} from "@/features/trips/components/TripCard";
import { TripsSort } from "@/features/trips/components/TripsSort";
import { tripsQueryOptions } from "@/features/trips/hooks/useTrips";
import type { TripsSortValue } from "@/features/trips/types/types";
import { initialSortValue } from "@/features/trips/utils/initialTripsValues";
import { rudderAnalytics, TripTrackingEvents } from "@/lib/rudderstack";

export function TripList() {
  const intl = useIntl();
  const navigate = useNavigate();
  const [countryFilter, setCountryFilter] = useState("");
  const [isSortDrawerOpen, setIsSortDrawerOpen] = useState(false);
  const [sortValue, setSortValue] = useState<TripsSortValue>(initialSortValue);

  const { data, isLoading, isError } = useQuery(
    tripsQueryOptions({
      sortField: sortValue.field,
      sortOrder: sortValue.order,
    }),
  );

  const countries = useMemo(() => {
    const allCountries = data?.flatMap((trip) => trip.countriesNames) || [];
    return Array.from(new Set(allCountries)).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const isEmpty = !isLoading && (!data || data.length === 0);

  useEffect(() => {
    if (data) {
      rudderAnalytics.track(TripTrackingEvents.USER_VIEWED_TRIP_LIST);
    }
  }, [data]);

  return (
    <div>
      <div>
        {isError ? (
          <ErrorPanel
            icon={IconAirplaneCrossed}
            primaryText={intl.formatMessage({
              defaultMessage: "An error occurred",
              description:
                "Heading shown when trip results fail to load due to an error",
            })}
            secondaryText={intl.formatMessage({
              defaultMessage:
                "We couldn't load trips at the moment. Please try again later.",
              description: "Description shown when trip results fail to load",
            })}
          />
        ) : isEmpty ? (
          <div className="mt-12 flex grow flex-col items-center justify-between gap-8">
            <ErrorPanel
              icon={IconAirplaneCrossed}
              primaryText={intl.formatMessage({
                defaultMessage: "No trips found",
                description:
                  "Heading shown when no trips match the search criteria",
              })}
              secondaryText={intl.formatMessage({
                defaultMessage: "We couldn't find any trips for you.",
                description:
                  "Description shown when no trips are found, prompting user to adjust criteria",
              })}
            />
          </div>
        ) : (
          <div className="w-full">
            <div className="pb-4">
              {isLoading ? (
                <Skeleton width="100%" height={57} variant="rounded" />
              ) : (
                <div className="flex w-full gap-2">
                  <Autocomplete
                    options={countries}
                    value={countryFilter || null}
                    placeholder={intl.formatMessage({
                      defaultMessage: "Search packages by country",
                      description:
                        "Button or filter to browse trips by destination country",
                    })}
                    startIcon={
                      <MagnifyingGlassIcon
                        size={20}
                        color="var(--color-text-disabled-tertiary)"
                      />
                    }
                    onChange={(value) => setCountryFilter(value ?? "")}
                    fullWidth
                  />

                  <ButtonBase
                    aria-label={intl.formatMessage({
                      defaultMessage: "Sorting",
                      description: "Label for the trips results sort selector",
                    })}
                    onClick={() => setIsSortDrawerOpen(true)}
                    type="button"
                    className="rounded-2xl border border-stroke-neutral-soft bg-surface-base-light p-[calc(var(--spacing)*4-1px)] text-text-tertiary"
                  >
                    <SortAscendingIcon size={24} color="currentColor" />
                  </ButtonBase>
                </div>
              )}
            </div>

            <ul className="space-y-3">
              {isLoading ? (
                <>
                  {times(3, (index) => (
                    <li key={index}>
                      <TripCardSkeleton />
                    </li>
                  ))}
                </>
              ) : (
                data
                  ?.filter(
                    (trip) =>
                      !countryFilter ||
                      trip.countriesNames.includes(countryFilter),
                  )
                  .map((trip) => (
                    <li key={trip.id}>
                      <TripCard
                        trip={trip}
                        onClick={() => {
                          navigate({
                            to: "/trips/$tripId",
                            params: { tripId: trip.id },
                          });
                        }}
                      />
                    </li>
                  ))
              )}
            </ul>
          </div>
        )}
      </div>

      <SwipeableDrawer
        open={isSortDrawerOpen}
        onClose={() => setIsSortDrawerOpen(false)}
      >
        <DrawerTopBar
          onCloseClick={() => setIsSortDrawerOpen(false)}
          title={intl.formatMessage({
            defaultMessage: "Sorting",
            description: "Label for the trips results sort selector",
          })}
        />
        <TripsSort
          value={sortValue}
          onChange={(value) => {
            setSortValue(value);
            setIsSortDrawerOpen(false);
          }}
        />
      </SwipeableDrawer>
    </div>
  );
}
